import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Input } from "../../../shadcn/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../shadcn/components/ui/table";

type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  actions?: (row: T) => React.ReactNode;
  searchComponent?: React.ReactNode;
  filterComponent?: React.ReactNode;
  loading?: boolean;
};

export default function EventStatusTable<T>({
  data,
  columns,
  actions,
  searchComponent,
  filterComponent,
  loading,
}: DataTableProps<T>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      uuid: false,
      approver: false,
    });
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: searchValue,
    },
  });

  const divRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (divRef.current) {
        setWidth(divRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, [window?.innerWidth, width]);

  return (
    <div
      ref={divRef}
      className={`pt-3 border border-brand-borderGreyTwo bg-white !important  `}
    >
      <div className="mobile:flex items-center justify-between text-sm pl-4 pr-5 pb-2 lg:pb-4">
        <p className="mb-2 mobile:mb-0 font-openSans font-semibold text-web-body-md leading-5 text-brand-bgBlack">
          Event Status
        </p>
        {searchComponent}
        {filterComponent}
      </div>
      <div className="mx-auto !important" style={{ width: `${width - 30}px` }}>
        <Table>
          <TableHeader className="border text-xs bg-brand-bgLightGrey">
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="h-[182px] text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody className="text-xs font-normal">
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="py-0">
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell key={cell.id}>
                        {index === 0 ? (
                          typeof cell.getValue() === "string" ? (
                            <span>{cell.getValue() as string}</span>
                          ) : (
                            <></>
                          )
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </TableCell>
                    ))}
                    {actions && <TableCell>{actions(row.original)}</TableCell>}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (actions ? 1 : 0)}
                    className="h-[182px] text-center"
                  >
                    No Auction Event found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </div>
    </div>
  );
}
